// src/context/site/KbStatusChip.jsx
//
// The status chip of GET /context/kb-status. Five states, copied verbatim from
// the API's closed vocabulary (see useKbStatus.KB_STATES); an unknown string is
// rendered raw in a neutral pill rather than mapped to anything — ⛔ never
// invent a sixth state. The two active states get an indeterminate spinner and
// `started_at`: the API deliberately publishes no progress numbers.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { FONT_SANS, RADII, SEMANTIC } from '../../constants/theme.js';
import { KB_STATES, isKbBusy } from './useKbStatus.js';

const STATE_KEY = {
  'Ready': 'ready',
  'Discovering': 'discovering',
  'Indexing': 'indexing',
  'Needs review': 'needs_review',
  'Failed': 'failed',
};

const PALETTE = {
  'Ready': SEMANTIC.verde,
  'Discovering': SEMANTIC.info,
  'Indexing': SEMANTIC.info,
  'Needs review': SEMANTIC.attenzione,
  'Failed': SEMANTIC.errore,
};

function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}

export default function KbStatusChip({ state, startedAt }) {
  const { t } = useTranslation('context');
  if (!state) return null;
  const known = KB_STATES.includes(state);
  const c = known ? PALETTE[state] : SEMANTIC.neutro;
  const label = known ? t(`site.status.${STATE_KEY[state]}`) : state;
  const busy = isKbBusy(state);
  const time = busy ? formatTime(startedAt) : '';
  return (
    <span
      role="status"
      data-state={state}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '6px',
        padding: '3px 10px', borderRadius: RADII.pill,
        fontSize: '11px', fontWeight: 600, fontFamily: FONT_SANS,
        background: c.bg, color: c.fg, whiteSpace: 'nowrap',
      }}
    >
      {busy && (
        <>
          <Loader2 size={12} style={{ animation: 'spin 0.8s linear infinite' }} aria-hidden="true" />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </>
      )}
      {label}
      {time && <span style={{ fontWeight: 400 }}>· {t('site.startedAt', { time })}</span>}
    </span>
  );
}
